import { ActionTree } from 'vuex';
import ApiUrl from '../../../api/url';
import { LicenseState, CapacityInfo, NewLicense, NewLicenseResult, Activate } from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { isHappenedOtherPartyError } from '../../error-type';
import { AppContext } from '../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../translator';
import router from '../../../router/index';
import ScreenUrl from '../../../router/url';

export const actions: ActionTree<LicenseState, RootState> = {
    /**
     * ライセンス検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} pageNumber ページ番号
     * @param {number} perPage ページ毎の表示件数
     * @param {sort<LicenseSortColumn>} sort ソート条件
     * @returns {Promise<void>}
     */
    async search({ dispatch, commit }, { pageNumber, perPage, sort } = {}) {
        const payload = {
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.License.INDEX, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SEARCH_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ライセンス登録前のエラー等のクリア
     * 前回の作成に失敗していた場合などに、エラーメッセージなどがVuex上に残っていたらクリアするため
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearError({ commit }) {
        commit(types.CLEAR_CREATE_ERROR);
    },

    /**
     * ライセンス登録
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {NewLicense} newUser 新規ライセンス情報
     * @returns {Promise<void>}
     */
    async create({ dispatch, commit, rootState }, newLicense: NewLicense) {
        try {
            commit(types.CREATE_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.License.CREATE, params: { ...newLicense } },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CREATE_SUCCESS, resData);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.License.CREATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CREATE_FAILURE, params);
        }
    },

    /**
     * アクティベーション処理前のエラー等のクリア
     * 前回アクティベーション処理に失敗していた場合などに、エラーメッセージなどがVuex上に残っていたらクリアするため
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearActivateError({ commit }) {
        commit(types.CLEAR_ACTIVATE_ERROR);
    },

    /**
     * アクティベーション処理
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @returns {Promise<void>}
     */
    async activate(
        { dispatch, commit, rootState },
        { license_id, activation }: { license_id: number; activation: Activate }
    ) {
        try {
            commit(types.ACTIVATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.License.ACTIVATE.replace(':id', license_id.toString()),
                    params: activation,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.ACTIVATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.License.ACTIVATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.ACTIVATE_FAILURE, params);
        }
    },

    /**
     * ライセンス詳細情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id ライセンスID
     * @returns {Promise<void>}
     */
    async show({ dispatch, commit }, id: number) {
        try {
            commit(types.SHOW_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.License.SHOW.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ライセンス使用状況取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async capacity({ dispatch, commit }) {
        try {
            commit(types.CAPACITY_START);
            const resData = await dispatch('auth/api', { method: 'get', url: ApiUrl.License.CAPACITY }, { root: true });
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CAPACITY_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.CAPACITY_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 適応ライセンス情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async active({ dispatch, commit }) {
        try {
            commit(types.ACTIVE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.License.ACTIVE,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.ACTIVE_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.ACTIVE_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ライセンス削除
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} id ライセンスID
     * @returns {Promise<void>}
     */
    async destroy({ dispatch, commit, rootState }, id: number) {
        try {
            commit(types.DESTROY_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'delete',
                    url: ApiUrl.License.DESTROY.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DESTROY_SUCCESS);

                // 一覧画面へ戻る
                router.push(ScreenUrl.License.INDEX);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.License.DESTROYED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DESTROY_FAILURE, params);
        }
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
